<script setup lang="ts">

import { ModalDefault, ModalView } from '@shared/ui/modals'
import { AppTextarea, InputPrimary, NativeSelect } from '@shared/ui/fields'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useModalDisciplineBalance } from '../../model/modal-discipline-balance.hook'
import { DisciplineBalanceAction } from '../../model/enums'

defineOptions({
  name: 'ModalDisciplineBalance',
})

const {
  refIsOpen,
  refIsSaving,
  refAction,
  refReasonOptions,
  refReason,
  refLessons,
  refLessonsMode,
  refComment,
  refCommentMode,
  lessonsMin,
  lessonsMax,
  commentMaxLength,
  setAction,
  setReason,
  setLessons,
  setComment,
  closeModal,
  submitBalance,
} = useModalDisciplineBalance()
</script>

<template>
  <ModalDefault
    :show="refIsOpen"
    :loading="refIsSaving"
    :view="ModalView.Tabs"
    title="Edit balance"
    @click:overlay="closeModal"
  >
    <div class="modal-discipline-balance">
      <div
        class="modal-discipline-balance__tabs"
        role="tablist"
        aria-label="Balance action"
      >
        <button
          type="button"
          role="tab"
          :aria-selected="refAction === DisciplineBalanceAction.TopUp"
          :class="['modal-discipline-balance__tab', { selected: refAction === DisciplineBalanceAction.TopUp }]"
          @click="setAction(DisciplineBalanceAction.TopUp)"
        >
          Top up balance
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="refAction === DisciplineBalanceAction.Remove"
          :class="['modal-discipline-balance__tab', { selected: refAction === DisciplineBalanceAction.Remove }]"
          @click="setAction(DisciplineBalanceAction.Remove)"
        >
          Remove from balance
        </button>
      </div>
      <NativeSelect
        name="balance_reason"
        label="Balance reason"
        required
        placeholder-enabled
        placeholder="Select here"
        :disabled="refIsSaving"
        :options="refReasonOptions"
        :value="refReason"
        @change="setReason"
      />
      <InputPrimary
        type="number"
        name="balance_lessons"
        label="Number of lessons"
        placeholder="Type here"
        required
        :min="lessonsMin"
        :max="lessonsMax"
        :mode="refLessonsMode"
        :disabled="refIsSaving"
        :value="refLessons"
        @update:value="setLessons"
      />
      <AppTextarea
        name="balance_comment"
        label="Comment"
        placeholder="Type here"
        :mode="refCommentMode"
        :disabled="refIsSaving"
        :value="refComment"
        :max-length="commentMaxLength"
        @input="setComment"
      />
    </div>
    <template #controls>
      <BtnPrimary
        v-if="refAction === DisciplineBalanceAction.TopUp"
        text="Add lessons"
        color="emerald"
        :disabled="refIsSaving || refLessonsMode === 'error'"
        @click="submitBalance"
      />
      <BtnPrimary
        v-else
        text="Remove lessons"
        color="red"
        :disabled="refIsSaving || refLessonsMode === 'error'"
        @click="submitBalance"
      />
      <BtnSecondary
        text="Cancel"
        color="black"
        :disabled="refIsSaving"
        @click="closeModal"
      />
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalDisciplineBalance.scss" />
